import { useCallback } from 'react';
import { useToast } from 'src/components/ui/use-toast';

// Shared toast-lifecycle helper for "close modal now, finish API call in
// background" submits. Used by Submit Outcome (RequestStatusChangeForm),
// Add Outcome (CreateOutcomeFormDialog), and Edit Revision
// (EditRevisionFormModal) to mask the ~8s backend write-and-fan-out cost
// on multi-outcome requests (issue 59 on ai-agents-backend-service-v1).
//
// Pattern:
//   1. Show a "Submitting…" toast (no auto-dismiss).
//   2. Run closeNow() to hide the modal immediately.
//   3. Run submit() in the background.
//   4. On resolve: dismiss the pending toast, show success/destructive
//      toast, and call onSuccess / onFailure for any post-submit cleanup
//      or modal re-open + setError logic.

type RunOpts<T> = {
  submit: () => Promise<T>;
  closeNow: () => void;
  onSuccess?: (result: T) => void;
  onFailure: (error: unknown) => void;
  submitting?: string;
  success?: string;
  failure?: string;
};

// The global http client (src/api/Http/Http.ts) already surfaces the
// backend's actual error message via toast for any non-422 failure, so a
// blocked-action response (e.g. "This request is assigned to another
// editor.") reaches the toast store with the real reason. But the toast
// store caps at one visible toast (TOAST_LIMIT in use-toast.ts), so the
// generic `opts.failure` toast fired below immediately overwrites it.
// Prefer the caught error's own message so the specific reason survives;
// fall back to `opts.failure` only when the error carries no message.
function getErrorMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  return undefined;
}

function useOptimisticSubmit() {
  const { toast } = useToast();
  return useCallback(
    <T,>(opts: RunOpts<T>) => {
      const pending = toast({
        description: opts.submitting ?? 'Submitting…',
        // Radix's default duration auto-dismisses after 5s; the API can
        // take longer than that, so push it well past any plausible
        // submit time. We dismiss explicitly on resolve.
        duration: 999999,
      });

      opts.closeNow();

      void (async () => {
        try {
          const result = await opts.submit();
          pending.dismiss();
          if (opts.success) {
            toast({ variant: 'success', description: opts.success });
          }
          opts.onSuccess?.(result);
        } catch (error) {
          pending.dismiss();
          toast({
            variant: 'destructive',
            description:
              getErrorMessage(error) ?? opts.failure ?? 'Submission failed — please retry.',
          });
          opts.onFailure(error);
        }
      })();
    },
    [toast],
  );
}

export { useOptimisticSubmit };
